import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, MenuItem, DialogActions } from '@mui/material';

import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  CustomTextField,
  CustomAutoComplete,
  CustomTextArea,
  CustomButton,
  LoadingSkeleton,
  HorizontalForm,
  ConfirmationDialog,
} from '../../../common/components';

import {
  usePaymentOrder,
  useCreatePaymentOrderWithMovements,
  useUpdatePaymentOrderWithMovements,
  usePrintPaymentOrder,
} from '../../../hooks/usePaymentOrders';
import { useLegalEntities } from '../../../hooks/useLegalEntities';
import { useMovementTypes } from '../../../hooks/useMovementTypes';
import { useBankAccounts } from '../../../hooks/useBankAccounts';
import { useMovementConcepts } from '../../../hooks/useMovementConcepts';
import { useFinanceCashAccounts } from '../../../hooks/useSupplierLedger';

import MovementDraftForm from './MovementDraftForm';
import PaymentOrderMovementsTable from './PaymentOrderMovementsTable';
import BeneficiarySelectWithCreate from '../../common/components/BeneficiarySelectWithCreate';

import {
  buildMovementPayload,
  calculateMovementsTotal,
  createDefaultOrderForm,
  createMovementDraft,
  createTempId,
  getMovementKey,
  parseAmountInput,
  toISODate,
} from '../utils/paymentOrdersUtils';
import { PAYMENT_ORDER_STATUS_FILTERS } from '../constants/paymentOrdersConstants';
import { formatCurrency } from '../../../utils/formatUtils';
import {
  COST_AREA_OPTIONS,
  COST_BEHAVIOR_OPTIONS,
  COST_TRACEABILITY_OPTIONS,
  CURRENCY_OPTIONS,
} from '../../constants/financeConstants';

const statusOptions = PAYMENT_ORDER_STATUS_FILTERS.filter((option) => option.value !== 'all');

const extractErrorMessage = (error) => {
  const responseData = error?.response?.data;

  if (responseData) {
    if (typeof responseData === 'string') {
      return responseData;
    }

    const rawError = responseData.error || responseData.detail;
    if (rawError && typeof rawError === 'string') {
      const valueErrorMatch = rawError.match(/Value error,\s*([^[]+)/i);
      if (valueErrorMatch && valueErrorMatch[1]) {
        return valueErrorMatch[1].trim();
      }
      const lines = rawError
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      const descriptiveLine =
        lines.find((line) => line.toLowerCase().includes('value error')) ||
        (lines.length ? lines[lines.length - 1] : undefined);
      if (descriptiveLine) {
        return descriptiveLine
          .replace(/.*Value error,\s*/i, '')
          .replace(/\[.*$/i, '')
          .trim();
      }
      return rawError;
    }

    if (responseData.message) {
      return responseData.message;
    }
  }

  return error?.message || 'Ocurrió un error al guardar la orden de pago';
};

const PaymentOrderDialog = ({
  open,
  onClose,
  paymentOrderId,
  onSuccess = () => {},
  onError = () => {},
}) => {
  const isEditing = Boolean(paymentOrderId);

  const [formData, setFormData] = useState(createDefaultOrderForm());
  const [movements, setMovements] = useState([]);
  const [movementDraft, setMovementDraft] = useState(
    createMovementDraft(createDefaultOrderForm().emission_date)
  );
  const [editingMovementKey, setEditingMovementKey] = useState(null);
  const [movementToRemove, setMovementToRemove] = useState(null);
  const [errors, setErrors] = useState({});

  const { data: legalEntities = [] } = useLegalEntities();
  const { data: movementTypes = [] } = useMovementTypes();
  const { data: bankAccounts = [] } = useBankAccounts();
  const { data: cashAccounts = [] } = useFinanceCashAccounts();
  const { data: movementConcepts = [] } = useMovementConcepts();
  const fundAccounts = useMemo(() => [
    ...bankAccounts.map((account) => ({ ...account, fund_type: 'bank' })),
    ...cashAccounts.map((account) => ({
      ...account,
      fund_type: 'cash',
      account_number: account.name,
      account_type: 'CASH',
    })),
  ], [bankAccounts, cashAccounts]);
  const orderPaymentConcept = useMemo(
    () =>
      movementConcepts.find(
        (concept) => concept.name && concept.name.toLowerCase() === 'orden de pago'
      ),
    [movementConcepts]
  );
  const orderPaymentConceptName = orderPaymentConcept?.name || 'Orden de Pago';

  const {
    data: paymentOrderDetail,
    isLoading: detailLoading,
    isFetching: detailFetching,
  } = usePaymentOrder(paymentOrderId, { enabled: Boolean(open && paymentOrderId) });

  const createMutation = useCreatePaymentOrderWithMovements();
  const updateMutation = useUpdatePaymentOrderWithMovements();
  const printMutation = usePrintPaymentOrder();

  const resetDialogState = useCallback(() => {
    const defaultForm = createDefaultOrderForm();
    setFormData(defaultForm);
    setMovements([]);
    setMovementDraft(
      createMovementDraft(
        defaultForm.emission_date,
        orderPaymentConceptName,
        orderPaymentConcept?.uuid || ''
      )
    );
    setEditingMovementKey(null);
    setMovementToRemove(null);
    setErrors({});
  }, [orderPaymentConceptName, orderPaymentConcept]);

  useEffect(() => {
    setMovementDraft((prev) => ({
      ...prev,
      concept: orderPaymentConceptName,
      uuid_movement_concept: orderPaymentConcept?.uuid || '',
    }));
  }, [orderPaymentConcept, orderPaymentConceptName]);

  useEffect(() => {
    if (!open) {
      resetDialogState();
      return;
    }

    if (isEditing && paymentOrderDetail?.payment_order) {
      const { payment_order: order, bank_movements: associatedMovements = [] } = paymentOrderDetail;
      const emissionDate = order.emission_date
        ? toISODate(new Date(order.emission_date))
        : createDefaultOrderForm().emission_date;

      const parsedTotalAmount =
        order.total_amount !== null && order.total_amount !== undefined
          ? parseAmountInput(String(order.total_amount))
          : null;

      setFormData({
        emission_date: emissionDate,
        uuid_legal_entity: order.uuid_legal_entity || '',
        applied_to_description: order.applied_to_description || '',
        observations: order.observations || '',
        status: order.status || 'pending',
        total_amount: parsedTotalAmount !== null ? parsedTotalAmount.toFixed(2) : '',
        cost_behavior: order.cost_behavior || '',
        cost_traceability: order.cost_traceability || '',
        cost_area: order.cost_area || '',
        payment_method: order.payment_method || '',
        currency: order.currency || 'ARS',
        exchange_rate_to_ars: order.exchange_rate_to_ars || '',
        total_amount_ars: order.total_amount_ars || '',
        net_payable_amount: order.net_payable_amount || '',
        net_payable_amount_ars: order.net_payable_amount_ars || '',
        uuid_cash_account: order.uuid_cash_account || '',
      });

      setMovements(
        associatedMovements.map((movement) => {
          const parsedAmount = parseAmountInput(movement.amount);
          const hasNumeric = parsedAmount !== null;
          const amountSign = hasNumeric ? (parsedAmount < 0 ? -1 : 1) : movement.amountSign ?? -1;
          const amountValue = hasNumeric
            ? Math.abs(parsedAmount).toString()
            : movement.amount ?? '';

          return {
            uuid: movement.uuid,
            tempId: movement.uuid,
            uuid_bank_account: movement.uuid_bank_account || '',
            uuid_cash_account: movement.uuid_cash_account || '',
            fund_account_id: movement.uuid_cash_account || movement.uuid_bank_account || '',
            uuid_movement_type: movement.uuid_movement_type || '',
            emission_date: movement.emission_date
              ? toISODate(new Date(movement.emission_date))
              : emissionDate,
            due_date: movement.due_date ? toISODate(new Date(movement.due_date)) : '',
            amount: amountValue,
            amountSign,
            voucher_number: movement.voucher_number || '',
            concept: orderPaymentConceptName,
            uuid_movement_concept: orderPaymentConcept?.uuid || movement.uuid_movement_concept || '',
            display_in_book: movement.display_in_book !== false,
            is_reconciled: Boolean(movement.is_reconciled),
            cost_behavior: movement.cost_behavior || order.cost_behavior || '',
            cost_traceability: movement.cost_traceability || order.cost_traceability || '',
            cost_area: movement.cost_area || order.cost_area || '',
            payment_method: movement.payment_method || order.payment_method || '',
            currency: movement.currency || order.currency || 'ARS',
          };
        })
      );

      setMovementDraft(
        createMovementDraft(
          emissionDate,
          orderPaymentConceptName,
          orderPaymentConcept?.uuid || ''
        )
      );
      setEditingMovementKey(null);
      setErrors({});
    }

    if (open && !isEditing) {
      resetDialogState();
    }
  }, [open, isEditing, paymentOrderDetail, resetDialogState, orderPaymentConcept, orderPaymentConceptName]);

  const movementTypeIdsInOrder = useMemo(
    () => new Set(movements.map((movement) => movement.uuid_movement_type).filter(Boolean)),
    [movements]
  );

  const availableMovementTypes = useMemo(
    () =>
      movementTypes.filter(
        (type) => type.display_in_orders || movementTypeIdsInOrder.has(type.uuid)
      ),
    [movementTypes, movementTypeIdsInOrder]
  );

  const selectedMovementType = useMemo(
    () => availableMovementTypes.find((type) => type.uuid === movementDraft.uuid_movement_type),
    [availableMovementTypes, movementDraft.uuid_movement_type]
  );
  const requiresFundAccount =
    selectedMovementType ? selectedMovementType.is_bank_movement_type !== false : true;

  const movementsTotal = useMemo(() => calculateMovementsTotal(movements), [movements]);
  const hasTotalValue = formData.total_amount !== '' && formData.total_amount !== null;
  const parsedOrderTotal = useMemo(
    () => (hasTotalValue ? parseAmountInput(formData.total_amount) : null),
    [formData.total_amount, hasTotalValue]
  );
  const totalDifference =
    hasTotalValue && parsedOrderTotal !== null ? parsedOrderTotal - movementsTotal : 0;
  const totalsMismatch = Math.abs(totalDifference) >= 0.01;

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'emission_date') {
      setMovementDraft((prev) => ({ ...prev, emission_date: value }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleDraftChange = (event) => {
    const { name, value } = event.target;
    setMovementDraft((prev) => {
      const next = {
        ...prev,
        [name]: value,
      };

      if (name === 'amount') {
        const rawValue = value?.toString() ?? '';
        const trimmedInput = rawValue.replace(/\s+/g, '');
        let sign = prev.amountSign ?? -1;
        let sanitized = trimmedInput.replace(/[^0-9.,+-]/g, '');

        if (sanitized.startsWith('-')) {
          sign = -1;
          sanitized = sanitized.slice(1);
        } else if (sanitized.startsWith('+')) {
          sign = 1;
          sanitized = sanitized.slice(1);
        }

        sanitized = sanitized.replace(/[+-]/g, '');

        next.amountSign = sign;
        next.amount = sanitized;
      }

      if (name === 'fund_account_id') {
        const account = fundAccounts.find((item) => item.uuid === value);
        next.uuid_bank_account = account?.fund_type === 'bank' ? value : '';
        next.uuid_cash_account = account?.fund_type === 'cash' ? value : '';
      }

      if (name === 'uuid_movement_type') {
        const typeInfo = movementTypes.find((type) => type.uuid === value);
        if (typeInfo?.is_bank_movement_type === false) {
          next.fund_account_id = '';
          next.uuid_bank_account = '';
          next.uuid_cash_account = '';
        }
      }

      return next;
    });

    if (errors.movementDraft?.[name]) {
      setErrors((prev) => ({
        ...prev,
        movementDraft: {
          ...(prev.movementDraft || {}),
          [name]: undefined,
        },
      }));
    }

    if (name === 'uuid_movement_type') {
      const typeInfo = movementTypes.find((type) => type.uuid === value);
      if (typeInfo?.is_bank_movement_type === false && errors.movementDraft?.fund_account_id) {
        setErrors((prev) => ({
          ...prev,
          movementDraft: {
            ...(prev.movementDraft || {}),
            fund_account_id: undefined,
          },
        }));
      }
    }
  };

  const handleDraftCheckboxChange = (event) => {
    const { checked } = event.target;
    setMovementDraft((prev) => ({ ...prev, display_in_book: !checked }));
  };

  const validateMovementDraft = () => {
    const draftErrors = {};
    if (!movementDraft.uuid_movement_type)
      draftErrors.uuid_movement_type = 'El tipo de movimiento es obligatorio';
    if (requiresFundAccount && !movementDraft.fund_account_id)
      draftErrors.fund_account_id = 'La cuenta o caja es obligatoria para este tipo de movimiento';
    const parsedAmount = parseAmountInput(movementDraft.amount);
    if (!movementDraft.amount || parsedAmount === null || parsedAmount === 0)
      draftErrors.amount = 'El importe es obligatorio y debe ser válido';
    if (!movementDraft.emission_date) draftErrors.emission_date = 'La fecha de emisión es obligatoria';
    if (!movementDraft.due_date) draftErrors.due_date = 'La fecha de vencimiento es obligatoria';
    if (!movementDraft.voucher_number) draftErrors.voucher_number = 'El número de comprobante es obligatorio';

    setErrors((prev) => ({ ...prev, movementDraft: draftErrors }));
    return Object.keys(draftErrors).length === 0;
  };

  const handleAddOrUpdateMovement = () => {
    if (!validateMovementDraft()) return;

    const baseMovement = {
      fund_account_id: requiresFundAccount ? movementDraft.fund_account_id || '' : '',
      uuid_bank_account: requiresFundAccount ? movementDraft.uuid_bank_account || '' : '',
      uuid_cash_account: requiresFundAccount ? movementDraft.uuid_cash_account || '' : '',
      uuid_movement_type: movementDraft.uuid_movement_type,
      emission_date: movementDraft.emission_date || formData.emission_date,
      due_date: movementDraft.due_date || '',
      amount: movementDraft.amount,
      amountSign: movementDraft.amountSign ?? -1,
      voucher_number: movementDraft.voucher_number,
      concept: orderPaymentConceptName,
      uuid_movement_concept: orderPaymentConcept?.uuid || movementDraft.uuid_movement_concept || '',
      display_in_book: movementDraft.display_in_book,
      is_reconciled: movementDraft.is_reconciled || false,
    };

    if (editingMovementKey) {
      setMovements((prev) =>
        prev.map((movement) =>
          getMovementKey(movement) === editingMovementKey
            ? { ...movement, ...baseMovement }
            : movement
        )
      );
    } else {
      setMovements((prev) => [
        ...prev,
        {
          ...baseMovement,
          tempId: createTempId(),
        },
      ]);
    }

    setMovementDraft(
      createMovementDraft(
        formData.emission_date,
        orderPaymentConceptName,
        orderPaymentConcept?.uuid || ''
      )
    );
    setEditingMovementKey(null);
    setErrors((prev) => ({ ...prev, movementDraft: {} }));
  };

  const handleEditMovement = (movement) => {
    if (movement.is_reconciled) {
      return;
    }
    setMovementDraft({
      fund_account_id: movement.fund_account_id || movement.uuid_cash_account || movement.uuid_bank_account || '',
      uuid_bank_account: movement.uuid_bank_account || '',
      uuid_cash_account: movement.uuid_cash_account || '',
      uuid_movement_type: movement.uuid_movement_type,
      emission_date: movement.emission_date || formData.emission_date,
      due_date: movement.due_date || '',
      amount: movement.amount,
      amountSign: movement.amountSign ?? -1,
      voucher_number: movement.voucher_number || '',
      concept: orderPaymentConceptName,
      uuid_movement_concept: orderPaymentConcept?.uuid || movement.uuid_movement_concept || '',
      display_in_book: movement.display_in_book !== false,
      is_reconciled: Boolean(movement.is_reconciled),
    });
    setEditingMovementKey(getMovementKey(movement));
  };

  const removeMovement = (movement) => {
    const key = getMovementKey(movement);
    setMovements((prev) => prev.filter((item) => getMovementKey(item) !== key));
    if (editingMovementKey === key) {
      setMovementDraft(
        createMovementDraft(
          formData.emission_date,
          orderPaymentConceptName,
          orderPaymentConcept?.uuid || ''
        )
      );
      setEditingMovementKey(null);
    }
  };

  const handleRequestRemoveMovement = (movement) => {
    if (movement.is_reconciled) {
      return;
    }
    setMovementToRemove(movement);
  };

  const handleConfirmRemoveMovement = () => {
    if (!movementToRemove) return;
    removeMovement(movementToRemove);
    setMovementToRemove(null);
  };

  const handleCancelRemoveMovement = () => {
    setMovementToRemove(null);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.emission_date) newErrors.emission_date = true;
    if (!formData.uuid_legal_entity) newErrors.uuid_legal_entity = true;
    const parsedTotal = parseAmountInput(formData.total_amount);
    if (!hasTotalValue || parsedTotal === null) {
      newErrors.total_amount = 'Debe ingresar un monto válido para la orden.';
    } else if (parsedTotal < 0) {
      newErrors.total_amount = 'El monto de la orden no puede ser negativo.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildMovementsPayload = () =>
    movements.map((movement) =>
      buildMovementPayload(
        {
          ...movement,
          uuid_legal_entity: formData.uuid_legal_entity || null,
          cost_behavior: formData.cost_behavior || null,
          cost_traceability: formData.cost_traceability || null,
          cost_area: formData.cost_area || null,
          payment_method: formData.payment_method || null,
          currency: formData.currency || 'ARS',
          exchange_rate_to_ars: formData.exchange_rate_to_ars || null,
        },
        formData.emission_date
      )
    );

  const buildPaymentOrderPayload = () => ({
    emission_date: new Date(formData.emission_date).toISOString(),
    uuid_legal_entity: formData.uuid_legal_entity,
    applied_to_description: formData.applied_to_description?.trim() || null,
    observations: formData.observations?.trim() || null,
    status: formData.status || 'pending',
    cost_behavior: formData.cost_behavior || null,
    cost_traceability: formData.cost_traceability || null,
    cost_area: formData.cost_area || null,
    payment_method: formData.payment_method || null,
    currency: formData.currency || 'ARS',
    exchange_rate_to_ars: formData.exchange_rate_to_ars || null,
    total_amount_ars: formData.total_amount_ars || null,
    net_payable_amount: formData.net_payable_amount || null,
    net_payable_amount_ars: formData.net_payable_amount_ars || null,
    uuid_cash_account: formData.uuid_cash_account || null,
    total_amount: (() => {
      const parsedTotal = parseAmountInput(formData.total_amount);
      const normalizedTotal = parsedTotal != null ? parsedTotal : 0;
      return Number(normalizedTotal.toFixed(2));
    })(),
  });

  const handleSave = async (printAfterSave = false) => {
    if (!validateForm()) return;

    const payload = {
      payment_order: buildPaymentOrderPayload(),
      bank_movements: buildMovementsPayload(),
    };

    try {
      let savedOrderUuid = paymentOrderId;

      if (isEditing) {
        const response = await updateMutation.mutateAsync({ id: paymentOrderId, data: payload });
        savedOrderUuid = response?.payment_order?.uuid || paymentOrderId;
      } else {
        const response = await createMutation.mutateAsync(payload);
        savedOrderUuid = response?.payment_order?.uuid;
      }

      let successMessage = 'Orden de pago guardada correctamente';
      let successSeverity = 'success';
      if (printAfterSave && savedOrderUuid) {
        try {
          await printMutation.mutateAsync(savedOrderUuid);
          successMessage = 'Orden de pago guardada e impresión generada';
        } catch (printError) {
          console.error('Error printing payment order:', printError);
          successMessage = 'Orden de pago guardada, pero no se pudo generar el PDF.';
          successSeverity = 'warning';
        }
      }

      onSuccess(successMessage, successSeverity);
      onClose();
    } catch (error) {
      console.error('Error saving payment order:', error);
      const apiMessage = extractErrorMessage(error);
      onError(apiMessage, 'error');
    }
  };

  const isLoading = detailLoading || detailFetching;
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isPrinting = printMutation.isPending;

  return (
    <DialogContainer open={open} onClose={onClose} size="large">
      <DialogTitle onClose={onClose}>
        {isEditing ? 'Editar orden de pago' : 'Nueva orden de pago'}
      </DialogTitle>
      <DialogBody sx={{ flexDirection: 'column', gap: 3 }}>
        {isLoading && isEditing ? (
          <LoadingSkeleton />
        ) : (
          <>
            <Box sx={{ ml: 3, fontWeight: 600, fontSize: 18 }}>Datos generales de la orden</Box>
            <HorizontalForm
              sx={{
                minHeight: 120,
                mb: 0,
                alignItems: 'flex-start',
                columnGap: 1.5,
                rowGap: 1.5,
                flexWrap: 'wrap',
              }}
              withBorder={false}
            >
              <CustomTextField
                label="Fecha de emisión"
                type="date"
                name="emission_date"
                value={formData.emission_date}
                onChange={handleFormChange}
                error={Boolean(errors.emission_date)}
                shrinkLabel
                sx={{ maxWidth: 280, mr: 1.5 }}
              />
              <BeneficiarySelectWithCreate
                label="Beneficiario"
                name="uuid_legal_entity"
                value={formData.uuid_legal_entity}
                onChange={handleFormChange}
                error={Boolean(errors.uuid_legal_entity)}
                legalEntities={legalEntities}
                width={450}
              />
              <CustomTextField
                label="Importe de la orden"
                name="total_amount"
                value={formData.total_amount}
                onChange={handleFormChange}
                size="medium"
                error={Boolean(errors.total_amount) || (hasTotalValue && totalsMismatch)}
                helperText={
                  errors.total_amount ||
                  (hasTotalValue && parsedOrderTotal === null
                    ? 'Ingrese un monto válido.'
                    : hasTotalValue && totalsMismatch
                    ? `La sumatoria de movimientos es ${formatCurrency(
                        movementsTotal,
                        2
                      )}. Diferencia: ${formatCurrency(Math.abs(totalDifference), 2)}.`
                    : '')
                }
                inputProps={{ inputMode: 'decimal' }}
                sx={{ maxWidth: 240, mr: 1.5, ml: 1.5 }}
              />
              {isEditing && (
                <CustomAutoComplete
                  label="Estado"
                  name="status"
                  value={formData.status}
                  onChange={handleFormChange}
                  sx={{ maxWidth: 250 }}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </CustomAutoComplete>
              )}
              <CustomAutoComplete
                label="Fijo/Variable"
                name="cost_behavior"
                value={formData.cost_behavior}
                onChange={handleFormChange}
                sx={{ maxWidth: 220, mr: 1.5 }}
              >
                <MenuItem value="">Sin clasificar</MenuItem>
                {COST_BEHAVIOR_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </CustomAutoComplete>
              <CustomAutoComplete
                label="Directo/Indirecto"
                name="cost_traceability"
                value={formData.cost_traceability}
                onChange={handleFormChange}
                sx={{ maxWidth: 220, mr: 1.5 }}
              >
                <MenuItem value="">Sin clasificar</MenuItem>
                {COST_TRACEABILITY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </CustomAutoComplete>
              <CustomAutoComplete
                label="Área"
                name="cost_area"
                value={formData.cost_area}
                onChange={handleFormChange}
                sx={{ maxWidth: 220, mr: 1.5 }}
              >
                <MenuItem value="">Sin clasificar</MenuItem>
                {COST_AREA_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </CustomAutoComplete>
              <CustomAutoComplete
                label="Moneda"
                name="currency"
                value={formData.currency}
                onChange={handleFormChange}
                sx={{ maxWidth: 140 }}
              >
                {CURRENCY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </CustomAutoComplete>
            </HorizontalForm>

            <Box sx={{ mt: 0, px: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Ingresar movimiento
              </Typography>
              <MovementDraftForm
                draft={movementDraft}
                errors={errors.movementDraft || {}}
                movementTypes={availableMovementTypes}
                bankAccounts={fundAccounts}
                onChange={handleDraftChange}
                onCheckboxChange={handleDraftCheckboxChange}
                onSubmit={handleAddOrUpdateMovement}
                requiresBankAccount={requiresFundAccount}
                isEditing={Boolean(editingMovementKey)}
              />
            </Box>

            <Box sx={{ px: 3 }}>
              <PaymentOrderMovementsTable
                movements={movements}
                movementTypes={movementTypes}
                bankAccounts={fundAccounts}
                onEdit={handleEditMovement}
                onRemove={handleRequestRemoveMovement}
                getMovementKey={getMovementKey}
                sx={{ minHeight: 400 }}
              />
            </Box>
            <Box sx={{ px: 3, pt: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'flex-end',
                  gap: 1.5,
                  pb: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ ml: 'auto', textAlign: { xs: 'left', sm: 'right' } }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: totalsMismatch ? 'error.main' : 'text.secondary',
                    }}
                  >
                    Sumatoria de movimientos: {formatCurrency(movementsTotal, 2)}
                  </Typography>
                  {hasTotalValue && totalsMismatch && (
                    <Typography variant="body2" color="error">
                      Verifique los importes antes de continuar.
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
            <HorizontalForm variant="twoRows" withBorder={false} sx={{ px: 3, pt: 2 }}>
              <CustomTextArea
                label="Para ser aplicado al pago de"
                name="applied_to_description"
                value={formData.applied_to_description}
                onChange={handleFormChange}
                minRows={2}
                sx={{ maxWidth: 600, mr: 1.5 }}
              />

              <CustomTextArea
                label="Observaciones"
                name="observations"
                value={formData.observations}
                onChange={handleFormChange}
                minRows={2}
                sx={{ maxWidth: 600 }}
              />
            </HorizontalForm>
          </>
        )}
      </DialogBody>
      <DialogActions sx={{ gap: 1.5, justifyContent: 'flex-end', px: 3, pb: 3 }}>
        <CustomButton
          variant="info"
          onClick={() => handleSave(true)}
          disabled={isSaving || isPrinting}
        >
          Guardar e imprimir
        </CustomButton>
        <CustomButton variant="base" onClick={() => handleSave(false)} disabled={isSaving}>
          Guardar
        </CustomButton>
        <CustomButton variant="warning" onClick={onClose} disabled={isSaving}>
          Cancelar
        </CustomButton>
      </DialogActions>

      <ConfirmationDialog
        open={Boolean(movementToRemove)}
        title="Eliminar movimiento"
        message="¿Seguro que deseas eliminar el movimiento de la orden?"
        onClose={handleCancelRemoveMovement}
        onConfirm={handleConfirmRemoveMovement}
        confirmText="Eliminar"
        confirmVariant="danger"
      />
    </DialogContainer>
  );
};

export default PaymentOrderDialog;
